import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { DailyMetricsTab } from './admin/DailyMetricsTab';
import { UserDetailsTab } from './admin/UserDetailsTab';
import { UserAnalyticsTab } from './admin/UserAnalyticsTab';
import { AffiliateAdminTab } from './admin/AffiliateAdminTab';
import { AgentUsageTab } from './admin/AgentUsageTab';
import { MarginTab } from './admin/MarginTab';

const VALID_TABS = ['metrics', 'users', 'analytics', 'affiliates', 'agent-usage', 'margin'] as const;
type TabKey = typeof VALID_TABS[number];

/** Analytics sub-tabs (Daily Metrics / User Details / User Analytics / Affiliates / Agent usage).
 *  Admin gate is handled by the parent AnalyticsTabs — do not duplicate it here. */
export const AnalyticsContent: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as TabKey | null;
  const activeTab: TabKey = tabParam && VALID_TABS.includes(tabParam) ? tabParam : 'metrics';

  const setActiveTab = (tab: TabKey) => {
    // Functional form: a plain object would drop the outer `?view=` and eject the
    // operator out of the Analytics tab entirely.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', tab);
        return next;
      },
      { replace: true }
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-neutral-900">Admin Dashboard</h2>
        <p className="text-neutral-600 mt-1">User signups, analytics, and system metrics</p>
      </div>

      <div className="border-b border-neutral-200 mb-6">
        <nav className="-mb-px flex space-x-8" aria-label="Analytics sub-tabs">
          {(
            [
              ['metrics', 'Daily Metrics'],
              ['users', 'User Details'],
              ['analytics', 'User Analytics'],
              ['affiliates', 'Affiliates'],
              ['agent-usage', 'Agent usage'],
              ['margin', 'Margin'],
            ] as [TabKey, string][]
          ).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === key
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'
              }`}
            >
              {label}
            </button>
          ))}
        </nav>
      </div>

      <div className={activeTab === 'metrics' ? '' : 'hidden'}><DailyMetricsTab /></div>
      <div className={activeTab === 'users' ? '' : 'hidden'}><UserDetailsTab /></div>
      <div className={activeTab === 'analytics' ? '' : 'hidden'}><UserAnalyticsTab /></div>
      <div className={activeTab === 'affiliates' ? '' : 'hidden'}><AffiliateAdminTab /></div>
      {/* Mounted only when active: the rollup is a fresh aggregation per open, not a cached list. */}
      {activeTab === 'agent-usage' && <AgentUsageTab />}
      {activeTab === 'margin' && <MarginTab />}
    </div>
  );
};
